'use client';

import type { PageContentProps } from '@routeroyal/theme-core';
import { MarkdownRenderer, useMarkdownHeadings } from '@routeroyal/ui-core';
import { StonePageTableOfContents } from './StonePageTableOfContents';
import Link from 'next/link';
import { useEffect, useState } from 'react';

/**
 * Stone Investment Page Content
 * 
 * Full-featured content renderer with Stone Investment's luxury aesthetic:
 * - Cormorant Garamond headings, DM Sans body
 * - Sidebar table of contents with active section tracking
 * - LEGAL / INFO type badge
 * - Print and copy-link actions
 * - "Need help?" footer for legal pages
 */
export function StoneInvestmentPageContent({ 
  page, 
  showTableOfContents = page.showTableOfContents ?? true,
  showLastUpdated = true,
  showPrintButton = false,
  locale = 'en',
  className = '',
  messages,
}: PageContentProps) {
  const msg = messages;
  const [activeHeadingId, setActiveHeadingId] = useState<string>();
  const [linkCopied, setLinkCopied] = useState(false);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  const lastUpdated = page.updatedAt 
    ? new Date(page.updatedAt).toLocaleDateString(locale === 'fr' ? 'fr-FR' : locale === 'es' ? 'es-ES' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  const allHeadings = useMarkdownHeadings(page.content, 'MARKDOWN');
  const headings = showTableOfContents ? allHeadings : [];

  useEffect(() => {
    if (!showTableOfContents || headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeadingId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings, showTableOfContents]);

  const handlePrint = () => window.print();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const stoneMessages = (messages as Record<string, unknown>)?.stone as Record<string, unknown> | undefined;
  const pageContentMessages = (stoneMessages?.pageContent || {}) as Record<string, string>;
  const pc = (key: string, fallback: string): string => pageContentMessages[key] || (msg as Record<string, string>)?.[key] || fallback;

  const lastUpdatedLabel = pc('lastUpdated', 'Last updated');
  const onThisPageLabel = pc('onThisPage', 'On this page');
  const needHelpTitle = pc('needHelpTitle', 'Need Help Understanding This Policy?');
  const needHelpText = pc('needHelpText', 'If you have any questions about this document or would like further clarification, please don\u2019t hesitate to contact us.');
  const contactLabel = pc('contactLabel', 'Contact Us');

  return (
    <article className={`max-w-4xl mx-auto px-6 lg:px-12 py-12 lg:py-20 ${className}`}>
      {/* Page Title */}
      <h1
        className="text-4xl lg:text-5xl xl:text-6xl tracking-[-0.02em] mb-6 text-stone-900"
        style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontWeight: 300 }}
      >
        {page.title}
      </h1>

      {page.excerpt && (
        <p
          className="text-lg text-stone-500 max-w-2xl mb-8"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {page.excerpt}
        </p>
      )}

      {/* Meta Bar */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-stone-400 mb-10 pb-8 border-b border-stone-200">
        {page.type && (
          <span className={`
            inline-flex items-center px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em]
            ${page.type === 'LEGAL'
              ? 'bg-stone-900/5 text-stone-700 border border-stone-200'
              : page.type === 'INFO'
              ? 'bg-stone-100 text-stone-600 border border-stone-200'
              : 'bg-stone-50 text-stone-500 border border-stone-200'
            }
          `}>
            {page.type === 'LEGAL' && (
              <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            )}
            {page.type}
          </span>
        )}

        {showLastUpdated && lastUpdated && (
          <span className="text-stone-400 font-light tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {lastUpdatedLabel}: {lastUpdated}
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-1.5 px-3 py-1.5 text-stone-500 hover:text-stone-800 border border-stone-200 hover:border-stone-300 transition-colors text-xs uppercase tracking-widest"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {linkCopied ? (
              <>
                <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-600">{pc('copied', 'Copied')}</span>
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span>{pc('copyLink', 'Copy link')}</span>
              </>
            )}
          </button>

          {showPrintButton && (
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-900 text-stone-100 hover:bg-stone-800 transition-colors text-xs uppercase tracking-widest"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span>{pc('print', 'Print')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Table of Contents */}
      {showTableOfContents && headings.length > 0 && (
        <div className="lg:hidden mb-8">
          <button
            onClick={() => setMobileTocOpen(!mobileTocOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-stone-50 border border-stone-200 text-sm text-stone-700 uppercase tracking-widest"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            <span>{onThisPageLabel}</span>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${mobileTocOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {mobileTocOpen && (
            <div className="mt-1 px-4 py-3 bg-stone-50 border border-stone-200">
              <StonePageTableOfContents
                headings={headings}
                {...(activeHeadingId && { activeId: activeHeadingId })}
                title=""
              />
            </div>
          )}
        </div>
      )}

      {/* Content Area with optional TOC sidebar */}
      <div className={`relative ${showTableOfContents && headings.length > 0 ? 'lg:grid lg:grid-cols-[1fr_240px] lg:gap-12' : ''}`}>
        {/* Desktop TOC */}
        {showTableOfContents && headings.length > 0 && (
          <aside className="hidden lg:block lg:order-2">
            <StonePageTableOfContents
              headings={headings}
              {...(activeHeadingId && { activeId: activeHeadingId })}
              title={onThisPageLabel}
            />
          </aside>
        )}

        {/* Content */}
        <div className="lg:order-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          <MarkdownRenderer
            content={page.content}
            contentFormat="MARKDOWN"
            theme="stone"
          />
        </div>
      </div>

      {/* Legal Footer */}
      {page.type === 'LEGAL' && (
        <div className="mt-16 pt-10 border-t border-stone-200">
          <div className="bg-stone-50 border border-stone-200 p-8">
            <div className="flex items-start gap-5">
              <div className="shrink-0 p-3 bg-stone-900/5 border border-stone-200">
                <svg className="w-5 h-5 text-stone-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3
                  className="text-xl text-stone-900 mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontWeight: 500 }}
                >
                  {needHelpTitle}
                </h3>
                <p
                  className="text-sm text-stone-500 mb-5 leading-relaxed"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {needHelpText}
                </p>
                <Link
                  href={`/${locale}/contact`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-stone-900 text-stone-100 hover:bg-stone-800 transition-colors text-xs uppercase tracking-[0.15em]"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {contactLabel}
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
